export interface GuideOption {
  key: string
  content: string
}

export interface GuideSection {
  section: string
  options: GuideOption[]
}

export interface GuideIndexItem {
  key: string
  label: string
  title: string
  content: string
}

// Centralized LD Elements dictionary. Keys are stable and used by tooltips.
const rawLdElementsIndex: Record<string, GuideIndexItem> = {
  roadmap: {
    key: 'roadmap',
    label: 'Roadmap',
    title: '🧭 Roadmap',
    content:
      `Preview the structure of your speech so the judge can follow along.
 - Keep it one or two sentences: "Framework, then two contentions, then voters."
 - If your tournament allows, do it off‑clock.
 - Do not argue here—just orient the judge to the order.`
  },
  hook: {
    key: 'hook',
    label: 'Hook',
    title: '🎣 Hook',
    content:
      `Optional. Capture attention and frame why the topic matters before you dive in.
 - Good hooks: a precise question, a striking statistic, or a short analogy.
 - Keep it under 10–15 seconds; substance and weighing still win rounds.`
  },
  framework: {
    key: 'framework',
    label: 'Framework',
    title: '⚖️ Framework',
    content:
      `Your weighing lens: how the judge should decide the round.
 - State your Value (end goal) and Criterion (decision rule).
 - Explain why this lens is preferable and how all offense should be evaluated under it.
 - Make application easy: explicitly connect later arguments back to the Criterion.`
  },
  value: {
    key: 'value',
    label: 'Value',
    title: '🎯 Value',
    content:
      `The ultimate moral goal (e.g., Justice, Human Dignity) that the debate should maximize.
 - Pick a value consistent with topic literature and your case.
 - Tie every major impact back to the Value through your Criterion.`
  },
  criterion: {
    key: 'criterion',
    label: 'Criterion',
    title: '📏 Criterion',
    content:
      `The practical decision rule for achieving the Value (e.g., Protecting Fundamental Rights).
 - Must be workable and comparative—it should tell the judge how to weigh impacts.
 - Prefer criteria that you can apply clearly to your contentions and your opponent’s.`
  },
  philosophy: {
    key: 'philosophy',
    label: 'Philosophy',
    title: '🧠 Philosophical Justification',
    content:
      `Optional. Briefly justify your V/C with an author or theory (e.g., Kant, Mill, Rawls).
 - Keep it to one or two lines; avoid turning the round into an evidence dump.
 - Use philosophy to clarify your weighing, not to obscure it.`
  },
  definitions: {
    key: 'definitions',
    label: 'Definitions',
    title: '📘 Definitions',
    content:
      `Clarify genuinely contestable terms and limit ambiguity.
 - Define only what affects ground or weighing; over‑definition invites theory/T abuse.
 - Source definitions if they are pivotal and likely to be contested.`
  },
  observations: {
    key: 'observations',
    label: 'Observations',
    title: '👁️ Observations',
    content:
      `State assumptions that structure the debate (actor, scope, burdens, stock issues).
 - Use observations to prevent talking past each other and to set expectations for clash.
 - Keep them functional and concise; they should aid weighing, not replace it.`
  },
  contentions: {
    key: 'contentions',
    label: 'Contentions',
    title: '💥 Contentions',
    content:
      `Present 2–3 clean, winning lines of offense.
 - Structure each: Claim → Warrant(s) → Impact(s).
 - Prefer depth over a laundry list; choose arguments you can extend and weigh clearly.
 - Make explicit links to your Criterion so the judge can ballot on them.`
  },
  claim: {
    key: 'claim',
    label: 'Claim',
    title: 'Claim (tagline)',
    content:
      `A concise assertion that headlines your argument and previews the logic.
 - One short sentence; avoid jargon. It should be weighable even before the evidence.`
  },
  subpoint: {
    key: 'subpoint',
    label: 'Subpoint',
    title: 'Subpoint',
    content:
      `A secondary supporting point underneath a main contention.

- Use subpoints for clarity when a contention has multiple distinct warrants.
- Keep each subpoint concise and tie it back to the main claim and criterion.`,
  },
  warrant: {
    key: 'warrant',
    label: 'Warrant',
    title: '🔗 Warrant',
    content:
      `The reasoning or evidence that makes the claim true.
 - Use credible sources and clear internal links; explain causality, not just correlation.
 - The best warrants anticipate and answer likely responses.`
  },
  impact: {
    key: 'impact',
    label: 'Impact',
    title: '🌍 Impact',
    content:
      `Explain why it matters and how the judge should weigh it.
 - Do impact calculus: magnitude • probability • timeframe • reversibility.
 - Always tie impacts back to your Criterion so they count under your framework.`
  },
  highlights: {
    key: 'highlights',
    label: 'Highlights',
    title: '✂️ Highlights',
    content:
      `Optional. Show pre‑cut cards and citations when the pool expects evidence (e.g., policy‑leaning judges).
 - Keep it quick and relevant—highlights support your warrants; they are not the speech.`
  },
  closing: {
    key: 'closing',
    label: 'Closing / Crystallization',
    title: '📣 Crystallizing Close',
    content:
      `Finish with 2–3 clear voters that apply your weighing lens: “Under our V/C, we win because…”.
 - Collapse to the cleanest path to the ballot and signpost it.
 - Make the decision rule explicit so the RFD writes itself.`
  }
}

// Collapse extra blank lines in markdown content to tighten tooltips/render
function collapseContent(s: string): string {
  return (s || '').replace(/\n\s*\n+/g, '\n').trim()
}

export const ldElementsIndex: Record<string, GuideIndexItem> = Object.fromEntries(
  Object.entries(rawLdElementsIndex).map(([key, item]) => [
    key,
    { ...item, content: collapseContent(item.content) },
  ]),
)

// Derived section that strings the dictionary together alphabetically for the Guidebook UI
const ldElementsReferenceSection: GuideSection = {
  section: 'LD Elements Reference',
  options: Object.keys(ldElementsIndex)
    .sort((a, b) => a.localeCompare(b))
    .map((k) => {
      const item = ldElementsIndex[k]
      return { key: item.title, content: item.content }
    })
}

const guideContent: GuideSection[] = [
  {
    section: 'General Information',
    options: [
      {
        key: 'Introduction',
        content: `
Welcome to our Debate Guidebook! This guide is designed to help you quickly grasp the essentials of debating, including different debate formats, core concepts, and UI interactions.

Whether you're brand new to debates or looking for a refresher, you'll find clear explanations and practical examples to get you started confidently.

Explore the sections on the left to discover more about each debate type and helpful tips for successful debating.
`,
      },
    ],
  },
  {
    section: 'Elements System (Lincoln Douglas)',
    options: [
      {
        key: 'About the Elements System',
        content: `
> The Elements System is a structured way to build, analyze, and coach Lincoln Douglas (LD) speeches. Each speech is composed of clear elements (e.g., Roadmap, Framework, Contentions), each with a purpose, best practices, and (often) common pitfalls. Elements marked "(Optional)" are powerful but context-dependent. Use this system to learn LD, prep faster, and self‑coach during rounds.

Legend (used throughout):
- 🧭 Roadmap  •  🎣 Hook  •  ⚖️ Framework  •  🎯 Value  •  📏 Criterion  •  🧠 Philosophy  •  📘 Definitions  •  👁️ Observations  •  💥 Contentions  •  🔗 Warrant  •  🌍 Impact  •  ✂️ Highlights  •  📣 Closing/Crystallization

General Guidance:
- Keep every element functional: it must serve the win condition under your Value/Criterion (V/C).
- Prefer clarity over density; judges reward arguments they can track and weigh.
- Flag optional elements explicitly so you can collapse when time gets tight.
`,
      },
      {
        key: '1AC – First Affirmative Constructive (6:00)',
        content: `
### Purpose
Establish the moral lens (V/C), define key terms, and present clean, winning offense (contentions) that weigh under your framework.

### Elements
- 🧭 Roadmap — one sentence to preview structure. Keep it off‑clock if your tournament allows.
- ⚖️ Framework — your weighing lens.
  - 🎯 Value: the ultimate moral goal (e.g., Human Dignity, Justice).
  - 📏 Criterion: the test to achieve the value (e.g., Protecting Fundamental Rights). Make it measurable.
  - 🧠 Philosophical Justification (Optional): briefly cite an author/theory to legitimize V/C (e.g., Kant, Mill). One line is enough.
- 📘 Definitions & 👁️ Observations — clarify terms, actor, scope, and any stock issues. Only define what is genuinely contestable.
- 💥 Contentions — 2–3 clean lines of offense.
  - Claim (tagline) → 🔗 Warrant(s) → 🌍 Impact(s).
  - Prefer 1–2 strong impacts that weigh cleanly under your Criterion.
- ✂️ Highlights (Optional) — show pre‑cut cards and citations if policy‑leaning judges expect it.
- 📣 Crystallizing Close — 2–3 voters preview (“Under our V/C, we win because…”).

### Time Guide (suggestion)
- Framework & Definitions: 1–1.5 min
- Contentions: 4–4.5 min
- Close: 15–30 sec

### Tips & Pitfalls
- Don’t over‑define; it invites T abuse. Define only what changes the round.
- V/C alignment: every impact should map to your Criterion—say it explicitly.
- Avoid four+ contentions; depth > laundry list.
`,
      },
      {
        key: '1NC – First Negative Constructive (7:00)',
        content: `
### Purpose
Contest the weighing (if needed) and create negative offense. Cleanly clash with AC contentions.

### Elements
- 🧭 Roadmap — briefly sequence Off‑case then On‑case.
- On‑case Rebuttals — go contention by contention:
  - Direct Refutation (hit the claim/warrant)
  - Impact Defense (mitigate magnitude/probability/timeframe)
  - Impact Turn (when safe)
- Off‑case (as appropriate):
  - Topicality (T) — interp, violation, standards, voters.
  - Theory — violation, standards, voters (use sparingly).
  - Kritik — Link → Impact → Alternative → Role of the Ballot.
  - Disadvantage — Uniqueness → Link → Internal Link → Impact.
  - Counterplan — Text → Solvency → Net Benefit.
- Overview (Optional) — meta‑weighing to orient the judge.

### Time Guide (suggestion)
- Off‑case: 3–4 min • On‑case: 2–3 min • Overview: ~30 sec

### Tips & Pitfalls
- Don’t spray new positions without time to extend later. Choose fewer, win bigger.
- If you press framework, provide a clear counter‑criterion and why it’s preferable.
`,
      },
      {
        key: 'Cross‑Ex (3:00 each) – After 1AC / After 1NC',
        content: `
### Purpose
Create commitments, expose holes, and set up your next speech.

### Elements
- Question Types:
  - Clarification — pin down definitions and scope.
  - Setup — build premises for your later extension.
  - Probing — find missing warrants, weak links, or dropped standards.
  - Concession — lock them into useful admissions.
- Time Management:
  - Lead with the highest‑value path to your win condition.
  - Short questions; force short answers.

### Tips
- Don’t argue; extract. Save analysis for your speech.
- Confirm cites or “author/date” if evidence framing matters to the judge.
`,
      },
      {
        key: '1AR – First Affirmative Rebuttal (4:00)',
        content: `
### Purpose
Stabilize AC, answer Off‑case, and set up clean voters.

### Elements
- Strategic Overview — collapse and signal priorities.
- Framework Defense — defend Value/Criterion and weighing.
- Off‑case Responses — answer T/Theory/K/DA/CP using short labels (non‑unique, no‑link, perms, impact turns).
- On‑case Extension — extend your best contentions under the Criterion.
- Collapse/Time Allocation — tell the judge where you’re going.

### Tips
- Don’t try to win everything. Pick a path and make it obvious.
`,
      },
      {
        key: '1NR – First Negative Rebuttal (4:00)',
        content: `
### Purpose
Lock in negative paths to the ballot and press AC weaknesses.

### Elements
- Global Overview — time allocation + weighing lens.
- Rebuild Off‑case — extend the best negative positions.
- On‑case Clash — remove AC solvency/warrants/impacts.
- Crystallization — 2–3 reasons to prefer the Neg.
- Rhetorical Close — persuasive line.

### Tips
- Don’t introduce new positions; extend and weigh existing ones.
`,
      },
      {
        key: '2AR – Second Affirmative Rebuttal (3:00)',
        content: `
### Purpose
Deliver a judge‑friendly ballot story for the Aff.

### Elements
- Quick Overview — final weighing lens (why your V/C wins).
- Refute NR Extensions — take out decisive Neg offense.
- Extend Decisive Aff Positions — the clean path to the ballot.
- Impact Calculus — magnitude • probability • timeframe • reversibility.
- Final Voters & Clincher — bullet the decision rule.

### Tips
- Tie every extension back to the Criterion — don’t assume the judge connects dots.
`,
      },
      {
        key: '2NR – Second Negative Rebuttal (3:00)',
        content: `
### Purpose
Deliver the negative’s ballot story; weigh relentlessly.

### Elements
- Quick Overview — final weighing lens.
- Refute Aff Extensions — eliminate their win paths.
- Extend Decisive Neg Positions — your cleanest route to the ballot.
- Impact Calculus — magnitude • probability • timeframe • reversibility.
- Final Voters & Clincher — make the decision easy.
`,
      },
      {
        key: 'Time Management & Strategic Decisions',
        content: `
### Time Allocation (Guidelines)
- **1AC**: Framework & Definitions (1–1.5), Contentions (4–4.5), Close (0.5)
- **1NC**: Off‑case (3–4), On‑case (2–3), Overview (0.5)
- **1AR/1NR**: Overview (0.5), Off‑case/On‑case (3), Voters (0.5)
- **2AR/2NR**: Overview (0.5), Refute/Extend (2), Voters (0.5)

### Strategic Choices
- When to Include Optional Elements: Hook, Philosophy, Observations when they clarify and persuade—skip if they crowd out weighing or offense.
- Collapse vs Extend: Collapse when you clearly lead; extend multiple paths only if they are well‑warranted and you have time to weigh.
- Focus Areas: Off‑case if your positions are stronger; On‑case if the AC is fragile; Framework when weighing wins the debate.
`,
      },
    ],
  },
  // Inject the dictionary-backed reference for clean rendering in the Guidebook
  ldElementsReferenceSection,
  {
    section: 'Debate Formats',
    options: [
      {
        key: 'Lincoln Douglas',
        content: `

Lincoln Douglas debates are one-on-one contests focused on values and ethical reasoning. Debaters argue over principles like justice and fairness, using philosophical frameworks to support their positions.

This format emphasizes clear, thoughtful argumentation and challenges participants to defend their personal beliefs.`,
      },
      //       {
      //         key: 'Free Form',
      //         content: `

      // Free Form debates offer flexibility, allowing debaters to structure their arguments creatively without strict guidelines. This open format rewards spontaneous thinking and dynamic presentation.

      // Participants have the freedom to adapt their style, making it ideal for those who prefer a less structured debate environment.`,
      //       },
      {
        key: 'Public Forum',
        content: `

Public Forum debates are designed for clarity and accessibility. Teams discuss current issues using clear language and straightforward arguments that are easy for audiences to follow.

This format emphasizes practical evidence and real-world implications over technical details.`,
      },
      {
        key: 'Team Policy',
        content: `

Team Policy debates involve two-on-two teams that analyze a specific policy proposal. Debaters present detailed evidence and strategic planning to weigh the pros and cons of a policy.

The focus is on thorough research and clear, structured argumentation that appeals to both experts and lay audiences.`,
      },
      {
        key: 'Quick Round',
        content: `

Quick Round debates are fast-paced and concise, usually featuring just two rounds of argument. Debaters must deliver their points rapidly, emphasizing clarity and brevity.

This format challenges participants to think quickly and communicate their ideas under strict time constraints.`,
      },
      //       {
      //         key: 'Extemporaneous',
      //         content: `

      // Extemporaneous debates center on current events with minimal preparation time. Debaters receive a topic and must quickly organize their thoughts into a coherent, persuasive speech.

      // This format tests general knowledge and the ability to articulate ideas effectively on the spot.`,
      //       },
    ],
  },
  {
    section: 'Debate Tools',
    options: [
      {
        key: 'Flow Notes',
        content: '',
      },
      {
        key: 'Evidence',
        content: '',
      },
      {
        key: 'Simple Poll',
        content: '',
      },
      {
        key: 'Multi-Criteria Poll',
        content: '',
      },
      {
        key: 'Ballot',
        content: '',
      },
    ],
  },
  {
    section: 'Participants Panel',
    options: [
      {
        key: 'Exporting Data',
        content: '',
      },
      {
        key: 'Debate Sequence',
        content: '',
      },
      {
        key: 'Chat',
        content: '',
      },
      {
        key: 'Participants',
        content: '',
      },
      {
        key: 'Settings',
        content: '',
      },
    ],
  },
]

export default guideContent
